import { FieldValidator, ValidationErrors } from "final-form";
import { round } from "lodash";

import { FieldValue } from "fond/form/fields/TextField/TextField";
import { parseFieldAsNumber } from "fond/form/util";
import { PRIMARY_WIRE_OPTIONS, SECONDARY_WIRE_OPTIONS, TRANSFORMER_SIZE_OPTIONS } from "fond/project/panels/MuPower/architecture/constants";
import { MuPowerArchitectureFormValues } from "fond/project/panels/MuPower/architecture/types";
import { PowerArchitecture, PrimaryWireName, SecondaryWireName, SystemOfMeasurement, TransformerSize } from "fond/types";
import { convertFeetToMeters, metersToFeetDisplay } from "fond/utils";
import { compose, minimum, required, requireNonZero } from "fond/utils/validation";

const TAB_FIELD_GROUPS = ["Transformers", "Primary", "Secondary"] as const;

/** Builds form values from an architecture API response object. */
export function toFormValues(architecture: PowerArchitecture, systemOfMeasurement: SystemOfMeasurement): MuPowerArchitectureFormValues {
  const { Transformers, PrimaryWires, SecondaryWires } = architecture;

  return {
    Transformers: {
      AverageMeterLoadAmps: Transformers.AverageMeterLoadAmps,
      Options: TRANSFORMER_SIZE_OPTIONS.map(({ Size, DefaultAverageAddressCount }) => ({
        Size,
        Enabled: !!Transformers.Options[Size],
        AverageAddressCount: Transformers.Options[Size]?.AverageAddressCount ?? DefaultAverageAddressCount,
      })),
    },
    Primary: {
      VoltageVolts: PrimaryWires.VoltageVolts,
      MaxSourceLoadAmps: PrimaryWires.MaxSourceLoadAmps,
      Options: PRIMARY_WIRE_OPTIONS.map(({ Name, DefaultMaxLoadAmps }) => ({
        Name,
        Enabled: !!PrimaryWires.Options[Name],
        MaxLoadAmps: PrimaryWires.Options[Name]?.MaxLoadAmps ?? DefaultMaxLoadAmps,
      })),
    },
    Secondary: {
      Options: SECONDARY_WIRE_OPTIONS.map(({ Name, DefaultAverageAddressCount }) => ({
        Name,
        Enabled: !!SecondaryWires.Options[Name],
        AverageAddressCount: SecondaryWires.Options[Name]?.AverageAddressCount ?? DefaultAverageAddressCount,
      })),
      MaxLength: systemOfMeasurement === "imperial" ? metersToFeetDisplay(SecondaryWires.MaxLengthMetres) : round(SecondaryWires.MaxLengthMetres, 4),
    },
  };
}

/** Builds an architecture from submitted form values. */
export function toPowerArchitecture(values: MuPowerArchitectureFormValues, systemOfMeasurement: SystemOfMeasurement): PowerArchitecture {
  const transformerSize = (size: TransformerSize) => {
    const row = values.Transformers.Options.find(({ Size }) => Size === size);
    return row?.Enabled ? { AverageAddressCount: parseFieldAsNumber(row.AverageAddressCount)! } : null;
  };
  const primaryWire = (name: PrimaryWireName) => {
    const row = values.Primary.Options.find(({ Name }) => Name === name);
    return row?.Enabled ? { MaxLoadAmps: parseFieldAsNumber(row.MaxLoadAmps)! } : null;
  };
  const secondaryWire = (name: SecondaryWireName) => {
    const row = values.Secondary.Options.find(({ Name }) => Name === name);
    return row?.Enabled ? { AverageAddressCount: parseFieldAsNumber(row.AverageAddressCount)! } : null;
  };
  const maxLength = parseFieldAsNumber(values.Secondary.MaxLength)!;

  return {
    Type: "power",
    Transformers: {
      AverageMeterLoadAmps: parseFieldAsNumber(values.Transformers.AverageMeterLoadAmps)!,
      Options: {
        kva_15: transformerSize("kva_15"),
        kva_25: transformerSize("kva_25"),
        kva_50: transformerSize("kva_50"),
        kva_75: transformerSize("kva_75"),
      },
    },
    PrimaryWires: {
      VoltageVolts: parseFieldAsNumber(values.Primary.VoltageVolts)!,
      MaxSourceLoadAmps: parseFieldAsNumber(values.Primary.MaxSourceLoadAmps)!,
      Options: {
        awg_1_0_al_1ph: primaryWire("awg_1_0_al_1ph"),
        awg_1_0_al_2ph: primaryWire("awg_1_0_al_2ph"),
        awg_1_0_al_3ph: primaryWire("awg_1_0_al_3ph"),
      },
    },
    SecondaryWires: {
      MaxLengthMetres: systemOfMeasurement === "imperial" ? convertFeetToMeters(maxLength) : maxLength,
      Options: {
        awg_4_0: secondaryWire("awg_4_0"),
        kcmil_350: secondaryWire("kcmil_350"),
      },
    },
  };
}

/** Parses a field value as a number, returning undefined for empty or unparseable values. */
const parseStrict = (value: FieldValue | undefined): number | undefined => {
  if (value == null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** The maximum secondary load (in amps): average meter load multiplied by the average number of addresses fed. */
export function calculateMaxSecondaryLoadAmps(
  averageMeterLoadAmps: FieldValue | undefined,
  averageAddressCount: FieldValue | undefined
): number | undefined {
  const load = parseStrict(averageMeterLoadAmps);
  const count = parseStrict(averageAddressCount);
  if (load === undefined || count === undefined) return undefined;

  return load * count;
}

/** Field validator requiring a positive, non-zero value (plus any extra validators) while the row's checkbox is enabled. */
export const validateWhenEnabled =
  (getEnabled: (values: MuPowerArchitectureFormValues) => boolean, ...extraValidators: FieldValidator<FieldValue>[]): FieldValidator<FieldValue> =>
  (value, allValues) =>
    getEnabled(allValues as MuPowerArchitectureFormValues)
      ? compose<FieldValue>(required, minimum(0), requireNonZero(), ...extraValidators)(value, allValues)
      : undefined;

/** Validator for a group field that errors unless at least one row is enabled. */
export const atLeastOneEnabled =
  (getRows: (values: MuPowerArchitectureFormValues) => { Enabled: boolean }[], message: string): FieldValidator<FieldValue> =>
  (_, allValues) =>
    getRows(allValues as MuPowerArchitectureFormValues).some(({ Enabled }) => Enabled) ? undefined : message;

const hasAnyError = (node: unknown): boolean => {
  if (typeof node === "string") return true;
  if (Array.isArray(node)) return node.some(hasAnyError);
  if (node !== null && typeof node === "object") return Object.values(node).some(hasAnyError);
  return false;
};

/** Returns the indices of tabs whose field groups hold validation errors. */
export function getTabsWithErrors(errors: ValidationErrors): number[] {
  return TAB_FIELD_GROUPS.flatMap((group, index) => (hasAnyError(errors?.[group]) ? [index] : []));
}
